import React, { useState } from "react";
import Printables from './printables';
import { Link } from "react-router-dom";
import { Header } from "../../../components/page-header/Header";

// icons
import { Image, Video, AudioLines, Upload, FileText, HardDrive, HandCoins } from 'lucide-react';
import UploadModal from "../../../components/Modal/UploadModal";
import { useTranslation } from "react-i18next";
import { useGetresourcesQuery } from "../../../store/Reducer/resources";

const KINDS = [
    { id: 'images', label: 'Images', icon: Image },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'audios', label: 'Audio', icon: AudioLines },
    { id: 'others', label: 'Documents', icon: FileText },
];

const size = (bytes) => {
    if (!bytes) return '0 KB';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / 1024 ** i).toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
};

const Resources = () => {
    const { t } = useTranslation();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const { data, isLoading } = useGetresourcesQuery({});
    const files = data?.data || {};
    const stats = KINDS.map((k) => ({
        ...k,
        count: (files[k.id] || []).length,
        bytes: (files[k.id] || []).reduce((sum, f) => sum + (f.fileSize || 0), 0),
    }));
    const total = stats.reduce((sum, s) => sum + s.bytes, 0);

    const breadcrumbItems = [
        { label: t("universal_dashboard_text"), to: '/user-dashboard' },
        { label: t("resources_breadcurmbs_title") },
    ];

    return (
        <>
            <section className="min-h-[86vh] bg-gradient-to-b from-slate-50 to-gray-50">
                <div className="max-w-7xl mx-auto p-4 sm:p-6">
                    <div className="space-y-6">

                        <div className="flex items-center justify-between">
                            <Header title={t("resources_title")} breadcrumbItems={breadcrumbItems} />
                        </div>

                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                            {/* Manage Media Assets */}
                            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md flex justify-between flex-col">
                                <div>
                                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                                        <Image className="w-5 h-5" />
                                        {t("resources_manage_Media_assets")}
                                    </h2>
                                    <ul className="space-y-1">
                                        {stats.map(({ id, label, icon: Icon, count }) => (
                                            <li key={id}>
                                                <Link to={`/user-dashboard/resources-media-management?tab=${id}`}
                                                    className="flex items-center justify-between rounded-lg px-2 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                                                    <span className="flex items-center gap-2"><Icon className="w-4 h-4" />{label}</span>
                                                    <span className="text-sm text-gray-500">{isLoading ? '…' : count}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <button onClick={() => setIsUploadModalOpen(true)} className="mt-4 flex items-center justify-center gap-2 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                                    <Upload className="w-4 h-4" />
                                    {t("resources_upload_media_button_text")}
                                </button>
                            </div>

                            {/* Printables */}
                            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
                                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5" />
                                    {t("resources_printables")}
                                </h2>
                                <div className="space-y-3">
                                    <Printables />
                                    <Link to="/user-dashboard/user-donations" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700">
                                        <HandCoins className="w-4 h-4" />
                                        Donation settings and QR code
                                    </Link>
                                </div>
                            </div>

                            {/* Storage Manager */}
                            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
                                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                                    <HardDrive className="w-5 h-5" />
                                    {t("resources_storage_manager")}
                                </h2>
                                <div className="flex justify-between text-sm mb-3">
                                    <span className="text-gray-600">Storage used</span>
                                    <span className="font-medium">{isLoading ? '…' : size(total)}</span>
                                </div>
                                <h3 className="text-sm font-medium mb-2">By type</h3>
                                <ul className="space-y-2">
                                    {stats.map(({ id, label, bytes, count }) => (
                                        <li key={id} className="p-2 bg-gray-50 rounded-lg">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-600">{label} <span className="text-gray-400">({count})</span></span>
                                                <span className="font-medium">{size(bytes)}</span>
                                            </div>
                                            <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                                                <div className="h-full rounded-full bg-blue-600" style={{ width: `${bytes ? Math.max(2, (bytes / total) * 100) : 0}%` }} />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Upload Modal */}
            <UploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
            />
        </>
    );
};

export default Resources;
